import type { ColumnDef } from '@tanstack/vue-table'
import { Button } from '~/components/ui/button'
import TemplateFileIcon from '~/components/TemplateFileIcon.vue'
import ExpandIcon from '~/components/ExpandIcon.vue'
import EyeIcon from '~/components/EyeIcon.vue'
import PenIcon from '~/components/PenIcon.vue'
import UserInitialsTableCell from '~/components/UserInitialsTableCell.vue'
import DataTableFilterableHeader from '~/components/ui/table/DataTableFilterableHeader.vue'

export const createColumns = (
  unfilteredData: EsgMetricFilterOptions,
  showAssetsModal: (assets: AssetItem[]) => void,
  canEdit: boolean = true,
): ColumnDef<EsgThemeConfiguration>[] => {
  const columns: ColumnDef<EsgThemeConfiguration>[] = [
    {
      accessorKey: 'name',
      id: 'metric',
      size: 394,
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'metric',
        filterData: unfilteredData.metrics,
      }),
      cell: ({ row, column }) => {
        const hasTemplateBeenUsed = row.original.data_collection_template
        const templateIconColor = hasTemplateBeenUsed ? 'text-main-primary' : 'text-gray-400'
        const title = hasTemplateBeenUsed ? `Using template ${row.original.data_collection_template?.name}` : 'No template selected for this metric'

        return h('div', { class: 'flex items-center gap-1.5' }, [
          h('span', { class: templateIconColor, title }, [
            h(TemplateFileIcon, { customStyle: 'size-4' }),
          ]),
          h('span', { class: 'text-left text-md text-foreground truncate no-localize-translate' }, row.getValue(column.id)),
        ])
      },
    },
    {
      accessorKey: 'assets',
      id: 'asset',
      size: 100,
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'asset',
        filterData: unfilteredData.assets,
      }),
      cell: ({ row, column }) => {
        const assets = row.getValue(column.id) as AssetItem[]
        return h('div', { class: 'flex justify-between items-center' }, [
          h('span', { class: 'capitalize text-left text-md text-foreground whitespace-nowrap' }, `${assets.length} Assets`),
          h('button', {
            class: 'p-4 hover:bg-slate-200 rounded-md',
            onClick: () => {
              showAssetsModal(assets)
            },
          }, [
            h(ExpandIcon, { class: 'h-4 w-4' }),
          ]),
        ])
      },
    },
    {
      accessorKey: 'topic.title',
      id: 'topic',
      size: 180,
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'topic',
        filterData: unfilteredData.topics,
      }),
      cell: ({ row, column }) => h('div', { class: 'text-left text-md text-foreground' }, row.getValue(column.id)),
    },
    {
      accessorKey: 'data_owners',
      id: 'data_owner',
      size: 200,
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'data owner',
        filterData: unfilteredData.data_owners,
      }),
      cell: ({ row, column }) => h(UserInitialsTableCell, { users: row.getValue(column.id) as { id: number, name: string }[] }),
    },
    {
      accessorKey: 'data_approvers',
      id: 'data_approver',
      size: 200,
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'data approver',
        filterData: unfilteredData.data_approvers,
      }),
      cell: ({ row, column }) => h(UserInitialsTableCell, { users: row.getValue(column.id) as { id: number, name: string }[] }),
    },
  ]

  // add actions column, but conditionally show edit button
  columns.push({
    id: 'actions',
    size: 50,
    header: () => h('span', { class: 'capitalize text-sm text-slate-800 font-semibold' }, 'actions'),
    cell: ({ row }) => {
      const hasTemplateBeenUsed = row.original.data_collection_template
      const iconColor = hasTemplateBeenUsed ? 'text-main-primary' : 'text-gray-400'
      const tooltipClasses = 'absolute hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 px-2 py-1 bg-zuno-primary-2 text-white text-xs rounded whitespace-nowrap mb-1 z-10'

      const actionButtons = [
        // preview button - always visible
        h('div', {
          'class': `relative group`,
          'data-tooltip': 'Preview metric',
        }, [
          h(Button, {
            class: `${iconColor} has-tooltip`,
            variant: 'link',
            size: 'sm',
            disabled: !hasTemplateBeenUsed,
            onClick: () => navigateTo(`/dashboard/esg-metrics-configuration/preview/${row.original.id}`),
          }, () => h(EyeIcon)),
          h('div', {
            class: tooltipClasses,
          }, 'Preview metric'),
        ]),
      ]

      // Edit button - only show if canEdit is true
      if (canEdit) {
        actionButtons.push(
          h('div', {
            class: 'relative group',
          }, [
            h(Button, {
              class: 'text-main-primary',
              variant: 'link',
              size: 'sm',
              onClick: () => navigateTo(`/dashboard/esg-metrics-configuration/edit/${row.original.id}`),
            }, () => h(PenIcon)),
            h('div', {
              class: tooltipClasses,
            }, 'Edit metric'),
          ]),
        )
      }

      return h('div', { class: 'flex items-center' }, actionButtons)
    },
  })

  return columns
}
